import React, { useEffect } from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import {
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { useAuth } from '../../context/AuthContext';

const plans = [
  {
    id: 1,
    name: 'Starter',
    price: 250,
    billing: 'Per Term',
    popular: false,
    features: [
      'Up to 500 Students',
      'Up to 20 Teachers',
      'Student Management',
      'Teacher Management',
      'Class Management',
      'Exam & Results',
      'Timetable',
      'Parent Portal',
      'Basic Reports',
    ],
  },
  {
    id: 2,
    name: 'Basic',
    price: 500,
    billing: 'Per Term',
    popular: true,
    features: [
      'Up to 750 Students',
      'Up to 30 Teachers',
      'Everything in Starter',
      'Attendance Tracking',
      'Library Management',
      'Inventory Management',
      'Improved Reports',
    ],
  },
  {
    id: 3,
    name: 'Pro',
    price: 1000,
    billing: 'Per Term',
    popular: false,
    features: [
      'Up to 1,500 Students',
      'Up to 100 Teachers',
      'Everything in Basic',
      'Advanced Finance Features',
      'Advanced Reports & Analytics',
      'Bulk Operations',
      'Priority Support',
      'More Storage',
    ],
  },
  {
    id: 4,
    name: 'Enterprise',
    price: 2000,
    billing: 'Per Term',
    popular: false,
    features: [
      'Unlimited Students',
      'Unlimited Teachers',
      'Everything in Pro',
      'Dedicated Onboarding & Training',
      'Custom Reports',
      'Dedicated Account Manager',
      'Highest Priority Support',
      'Maximum Storage Allocation',
    ],
  },
];

const SkeletonCard = () => (
  <div className="bg-white border border-slate-200 rounded-3xl p-8 flex flex-col h-full animate-pulse">
    <div className="h-8 w-24 mx-auto bg-slate-200 rounded mb-6" />
    <div className="h-12 w-32 mx-auto bg-slate-200 rounded mb-2" />
    <div className="h-4 w-20 mx-auto bg-slate-200 rounded mb-8" />

    <div className="space-y-4 flex-1 mb-10">
      {Array.from({ length: 6 }).map(
        (_, index) => (
          <div
            key={index}
            className="flex items-center gap-3"
          >
            <div className="w-5 h-5 bg-slate-200 rounded-full" />
            <div className="h-4 w-3/4 bg-slate-200 rounded" />
          </div>
        )
      )}
    </div>

    <div className="h-12 w-full bg-slate-200 rounded-2xl" />
  </div>
);

export default function Subscribe() {
  const navigate = useNavigate();

  const {
    loading,
    authReady,
    isAuthenticated,
    hasPaidPlan,
    logout,
  } = useAuth();

  const isLoading = loading || !authReady;

  useEffect(() => {
    if (!authReady || loading) return;

    if (!isAuthenticated) {
      navigate(
        '/login?redirect=%2Fsubscribe_to_plan',
        { replace: true }
      );

      return;
    }

    if (hasPaidPlan) {
      navigate('/app/dashboard', {
        replace: true,
      });
    }
  }, [
    authReady,
    loading,
    isAuthenticated,
    hasPaidPlan,
    navigate,
  ]);

  const handleSelectPlan = (plan) => {
    if (
      isLoading ||
      !isAuthenticated ||
      hasPaidPlan
    ) {
      return;
    }

    localStorage.setItem(
      'selectedPlan',
      JSON.stringify(plan)
    );

    navigate(
      `/app/checkout?planId=${plan.id}`
    );
  };

  const handleClose = () => {
    // Closing the subscription bridge signs the user out.
    // It never allows access to the protected application.
    logout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <button
        type="button"
        onClick={handleClose}
        className="fixed top-6 right-6 z-50 p-3 rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-100"
        aria-label="Sign out and close"
      >
        <XMarkIcon className="w-7 h-7 text-slate-600" />
      </button>

      <div className="max-w-7xl mx-auto px-6 pt-20 pb-16">
        <div className="text-center mb-12">
          <span className="inline-flex px-4 py-2 rounded-full bg-primary-50 text-primary-700 text-sm font-semibold">
            Activate your school
          </span>

          <h1 className="mt-5 text-4xl md:text-5xl font-bold text-slate-800 tracking-tight">
            Choose Your Plan
          </h1>

          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Select and purchase a plan before
            entering the PawaSchool dashboard.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {isLoading
            ? Array.from({ length: 4 }).map(
                (_, index) => (
                  <SkeletonCard key={index} />
                )
              )
            : plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative bg-white border rounded-3xl p-8 flex flex-col h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    plan.popular
                      ? 'border-primary-500 ring-2 ring-primary-100'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {plan.popular && (
                    <span className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gradient-to-r from-primary-500 to-secondary-600 text-white text-xs font-bold px-6 py-1.5 rounded-full shadow-md">
                      MOST POPULAR
                    </span>
                  )}

                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-slate-800">
                      {plan.name}
                    </h3>

                    <div className="mt-6 flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-extrabold text-primary-600">
                        {plan.price.toLocaleString()}
                      </span>

                      <span className="text-slate-400 font-medium text-xl">
                        SLE
                      </span>
                    </div>

                    <p className="text-sm text-slate-500 mt-1">
                      {plan.billing}
                    </p>
                  </div>

                  <ul className="space-y-4 flex-1 mb-10">
                    {plan.features.map(
                      (feature, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-3 text-slate-600"
                        >
                          <CheckCircleIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />

                          <span>{feature}</span>
                        </li>
                      )
                    )}
                  </ul>

                  <button
                    type="button"
                    onClick={() =>
                      handleSelectPlan(plan)
                    }
                    disabled={isLoading}
                    className={`w-full py-4 rounded-2xl font-semibold transition-all disabled:opacity-50 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-600 text-white hover:shadow-lg'
                        : 'border-2 border-slate-300 text-slate-700 hover:border-primary-500 hover:text-primary-600'
                    }`}
                  >
                    Select {plan.name}
                  </button>
                </div>
              ))}
        </div>

        <p className="text-center mt-14 text-sm text-slate-500">
          You will receive complete access immediately
          after your payment is verified.
        </p>
      </div>
    </div>
  );
}